import { useEffect, useState } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { getCachedSqliteSpatial, probeSqliteSpatial } from '../utils/sqliteSpatialProbe';

/**
 * Whether the SpatiaLite runtime is available on this SQLite connection —
 * the `JsonOperatorContext.sqliteSpatialAvailable` input that gates SQLite's
 * native spatial filter chips (its static capability is `false`). Probes once
 * per connection through the shared cached probe, so Table Explorer's
 * `FilterWizard` and the Visual Query Editor's filter chip read the same
 * answer. `undefined` for every other engine and while the probe is in flight.
 */
export function useSqliteSpatialAvailable(
  engine: DatabaseEngineId | undefined,
  connectionId: string | undefined,
  database: string | undefined,
): boolean | undefined {
  const [available, setAvailable] = useState<boolean | undefined>(() =>
    engine === 'sqlite' && connectionId ? getCachedSqliteSpatial(connectionId) : undefined,
  );
  useEffect(() => {
    if (engine !== 'sqlite' || !connectionId || !database) {
      setAvailable(undefined);
      return;
    }
    let alive = true;
    probeSqliteSpatial(connectionId, database)
      .then((ok) => {
        if (alive) setAvailable(ok);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [engine, connectionId, database]);
  return available;
}
